import classNames from 'classnames';
import { ReactNode, useContext } from 'react';
import { Autoplay, Controller, Keyboard, Navigation, Pagination } from 'swiper';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import SwiperClass from 'swiper/types/swiper-class';

import { Photo, WithClassName } from '@types';
import {
  formatDate,
  formatLocation,
  formatPhotoName,
  renderPeople,
} from '@utils/formatters';
import { IntlContext } from '@utils/useIntl';
import { PhotoMeta } from './meta';

const defaultRenderPhoto = (photo: Photo) => (
  <a
    className="flex items-center justify-center min-h-0"
    href={`/img/${photo.id}.jpg`}
    target="_blank"
  >
    <img
      className="max-w-full max-h-full shadow-xl"
      src={`/img/${photo.id}.jpg`}
      alt={formatPhotoName(photo)}
    />
  </a>
);

interface SliderProps {
  autoplay?: boolean;
  initialSlide?: number;
  onSlideChange?: (id: number, replace?: boolean) => void;
  photos: Photo[];
  renderPhoto?: (photo: Photo) => ReactNode;
  setControlledSwiper?: (swiper: SwiperClass) => void;
  swiperProps?: SwiperProps;
  withoutDetails?: boolean;
  withoutMeta?: boolean;
}

const Slider = ({
  autoplay,
  className = 'w-full h-full',
  initialSlide = 0,
  onSlideChange,
  photos,
  renderPhoto = defaultRenderPhoto,
  setControlledSwiper,
  swiperProps = {},
  withoutDetails,
  withoutMeta,
}: SliderProps & WithClassName) => {
  const { locale } = useContext(IntlContext);

  const navClassNames = 'top-0! m-0! w-4! desktop:w-7! h-full!';
  const arrowClassNames =
    'w-3 desktop:w-5 aspect-square rotate-45 border border-accent';
  const bulletClassNames = classNames(
    'w-5 h-5 p-2 transition-all',
    'before:w-full before:block before:h-full before:rounded-full before:bg-gray-mid before:transition-colors',
    'hover:p-1.5 hover:before:bg-gray',
  );
  const bulletActiveClassNames = 'p-1.5! before:bg-accent!';

  return (
    <Swiper
      className={classNames('relative', className)}
      initialSlide={initialSlide}
      slidesPerView="auto"
      centeredSlides
      grabCursor
      keyboard
      loop
      navigation={{
        prevEl: '.swiper-button-prev',
        nextEl: '.swiper-button-next',
      }}
      autoplay={autoplay ? { delay: 3000 } : undefined}
      pagination={
        autoplay
          ? {
              el: '#pagination',
              type: 'bullets',
              bulletClass: bulletClassNames,
              bulletActiveClass: bulletActiveClassNames,
              clickable: true,
            }
          : undefined
      }
      modules={[Autoplay, Controller, Keyboard, Navigation, Pagination]}
      onSlideChangeTransitionStart={({ realIndex }) => {
        if (initialSlide !== realIndex) {
          onSlideChange && onSlideChange(realIndex);
        }
      }}
      onSwiper={setControlledSwiper}
      {...swiperProps}
    >
      {photos.map((photo) => (
        <SwiperSlide
          key={photo.id}
          className={classNames(
            'flex max-desktop:flex-col justify-center max-desktop:gap-4 h-full px-6 desktop:px-12',
            autoplay ? 'pb-6 desktop:py-12' : 'py-4 desktop:py-10',
          )}
          itemScope
          itemType="https://schema.org/Photograph"
        >
          {/* Meta */}
          {!withoutMeta && <PhotoMeta photo={photo} />}

          {/* Left */}
          {!withoutDetails && (
            <div className="grow basis-0 flex flex-col items-center justify-center">
              {photo.name && (
                <p className="desktop:hidden text-xl font-title text-accent lowercase">
                  {photo.name}
                </p>
              )}
            </div>
          )}
          {/* Center */}
          {renderPhoto(photo)}
          {/* Right */}
          {!withoutDetails && (
            <div
              className={classNames(
                'grow basis-0 flex flex-col max-desktop:items-center justify-center gap-2 desktop:gap-4',
                'desktop:min-w-[300px] desktop:pl-10 desktop:text-left',
                'max-desktop:text-center max-desktop:text-sm',
              )}
            >
              {photo.name && (
                <p className="max-desktop:hidden text-2xl font-title text-accent lowercase">
                  {photo.name}
                </p>
              )}
              {photo.people && (
                <p className="text-2xl font-title text-accent lowercase">
                  {renderPeople(photo.people)}
                </p>
              )}
              {typeof photo.location === 'string' ? (
                <p>{photo.location}</p>
              ) : (
                <p>
                  {photo.location.detail && (
                    <>
                      <span>{photo.location.detail}</span>
                      <span className="desktop:hidden">{' — '}</span>
                      <br className="max-desktop:hidden" />
                    </>
                  )}
                  {formatLocation(photo.location, true)}
                </p>
              )}
              <p className="text-xs desktop:text-sm italic">
                {formatDate(photo.date, locale)}
              </p>
            </div>
          )}
        </SwiperSlide>
      ))}

      {autoplay && (
        <div
          id="pagination"
          className="absolute bottom-0! h-6 flex items-center justify-center z-10 cursor-pointer"
        />
      )}

      {/* Prev */}
      <div
        className={classNames('swiper-button-prev', navClassNames, 'left-0!')}
      >
        <div className={classNames(arrowClassNames, 'border-t-0 border-r-0')} />
      </div>
      {/* Next */}
      <div
        className={classNames('swiper-button-next', navClassNames, 'right-0!')}
      >
        <div className={classNames(arrowClassNames, 'border-b-0 border-l-0')} />
      </div>
    </Swiper>
  );
};

export default Slider;
